
import React from 'react';
import { ColoredProgress } from '@/components/ui/colored-progress';
import { Loader2 } from 'lucide-react';

interface ZoneDistribution {
  green: number;
  yellow: number;
  orange: number;
  red: number;
  processedCount?: number; // Add this optional field to track processed count
  totalCount?: number;     // Add total investor count
  isProcessing?: boolean;  // Add flag to show processing status
}

interface ZoneDistributionChartProps {
  distribution: ZoneDistribution;
}

export const ZoneDistributionChart: React.FC<ZoneDistributionChartProps> = ({ distribution }) => {
  const { 
    green, 
    yellow, 
    orange, 
    red, 
    processedCount = 0, 
    totalCount = 0, 
    isProcessing = false 
  } = distribution;
  
  // If no investors have been processed, show empty or processing state
  if (processedCount === 0) {
    if (isProcessing) {
      return (
        <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mb-4" />
          <p>Processing investor data...</p>
        </div>
      );
    }
    
    return (
      <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
        <p>No investor data available yet</p>
        <p className="text-sm mt-2">Process investors to see distribution</p>
      </div>
    );
  }
  
  // If some investors are processed but still processing more
  const processingInfo = isProcessing && totalCount > 0 ? (
    <div className="text-sm text-muted-foreground flex items-center mb-4">
      <Loader2 className="h-3 w-3 animate-spin mr-2" />
      Processing: {processedCount} of {totalCount} investors
    </div>
  ) : processedCount > 0 && totalCount > 0 ? (
    <div className="text-sm text-muted-foreground mb-4">
      {processedCount} of {totalCount} investors processed
    </div>
  ) : null;
  
  return (
    <div className="space-y-4">
      {processingInfo}
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Green Zone (7.5-10)</span>
          <span className="font-medium">{green}%</span>
        </div>
        <ColoredProgress value={green} className="h-2 bg-gray-100" color="green" />
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Yellow Zone (5-7.4)</span>
          <span className="font-medium">{yellow}%</span>
        </div>
        <ColoredProgress value={yellow} className="h-2 bg-gray-100" color="yellow" />
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Orange Zone (2.5-4.9)</span>
          <span className="font-medium">{orange}%</span>
        </div>
        <ColoredProgress value={orange} className="h-2 bg-gray-100" color="orange" />
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Red Zone (0-2.4)</span>
          <span className="font-medium">{red}%</span>
        </div>
        <ColoredProgress value={red} className="h-2 bg-gray-100" color="red" />
      </div>
    </div>
  );
};
